import {type ReactNode, useLayoutEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {getAnalyticsSessionId} from "@pg-mono/algolytics";

import {CloseButton} from "../../atoms/chat_bot/feedback_popup/components/CloseButton";
import {SemanticSearchSurfacePhase} from "../state/semantic_search_modal_slice";
import {getSemanticSearchModalSize} from "../tracking/get_semantic_search_modal_size";
import {trackAmplitudeOpenAiChat} from "../tracking/track_amplitude_open_ai_chat";

interface Props {
    phase: SemanticSearchSurfacePhase;
    onClose: () => void;
    halfSheetChildren: ReactNode;
    children: (overlayRef: React.RefObject<HTMLDivElement | null>) => ReactNode;
}

export const SemanticSearchFullscreenOverlay = ({phase, onClose, halfSheetChildren, children}: Props) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const isOpen = phase !== "closed";
    const isFullscreen = phase === "fullscreen";

    const [hasFinishedGrowth, setHasFinishedGrowth] = useState(isFullscreen);
    const previousPhaseRef = useRef(phase);

    useLayoutEffect(() => {
        const previousPhase = previousPhaseRef.current;
        previousPhaseRef.current = phase;

        if (phase === "halfSheet") {
            setHasFinishedGrowth(false);
        } else if (phase === "fullscreen" && previousPhase !== "halfSheet") {
            setHasFinishedGrowth(true);
        }

        const modalSize = getSemanticSearchModalSize(phase);

        if (modalSize && phase !== previousPhase) {
            trackAmplitudeOpenAiChat(modalSize, getAnalyticsSessionId());
        }
    }, [phase]);

    useLayoutEffect(() => {
        if (!isFullscreen || !overlayRef.current) return;

        const element = overlayRef.current;
        element.scrollTop = element.scrollHeight;
    }, [isFullscreen]);

    useLayoutEffect(() => {
        if (!isOpen) return;

        const previousOverflow = document.body.style.overflow;
        const previousOverscrollBehavior = document.body.style.overscrollBehavior;

        document.body.style.overflow = "hidden";
        document.body.style.overscrollBehavior = "none";

        return () => {
            document.body.style.overflow = previousOverflow;
            document.body.style.overscrollBehavior = previousOverscrollBehavior;
        };
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className={backdrop}
                        onClick={!isFullscreen ? onClose : undefined}
                        initial={{opacity: 0}}
                        animate={{opacity: 0.75}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.3}}
                    />

                    <motion.div
                        layoutRoot
                        className={layoutRootStyle}
                        initial={{y: "100%"}}
                        animate={{y: 0}}
                        exit={{y: "100%"}}
                        transition={{type: "tween", duration: 0.25, ease: "easeOut"}}
                    >
                        <motion.div
                            layout
                            className={cx(card, isFullscreen ? cardFullscreen : cardHalfSheet)}
                            role="dialog"
                            aria-modal="true"
                            aria-label="Asystent AI"
                            transition={{type: "tween", duration: 0.25, ease: "easeOut"}}
                            onLayoutAnimationComplete={() => {
                                if (isFullscreen) {
                                    setHasFinishedGrowth(true);
                                }
                            }}
                        >
                            <div className={cx(cardClip, isFullscreen ? cardFullscreenRadius : cardHalfSheetRadius)}>
                                <div ref={overlayRef} className={overlay} data-testid="ai-advisor-modal">
                                    {!hasFinishedGrowth && (
                                        <motion.div className={contentLayer} animate={{opacity: isFullscreen ? 0 : 1}} transition={{duration: 0.15}}>
                                            {halfSheetChildren}
                                        </motion.div>
                                    )}

                                    {hasFinishedGrowth && (
                                        <motion.div className={contentLayer} initial={{opacity: 0}} animate={{opacity: 1}} transition={{duration: 0.15}}>
                                            {children(overlayRef)}
                                        </motion.div>
                                    )}
                                </div>
                            </div>

                            {!hasFinishedGrowth && <CloseButton onClose={onClose} className={halfSheetCloseButton} iconSize="2.4" />}
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

const backdrop = css({
    position: "fixed",
    inset: 0,
    zIndex: 9000,
    background: "var(--colors-secondary)"
});

const layoutRootStyle = css({
    position: "fixed",
    inset: 0,
    zIndex: 9001,
    pointerEvents: "none"
});

const card = css({
    position: "absolute",
    left: 0,
    right: 0,
    boxSizing: "border-box",
    pointerEvents: "auto"
});

// Mutually exclusive with cardFullscreen - both set top/bottom/height, never applied together
const cardHalfSheet = css({
    bottom: 0,
    top: "auto",
    height: "auto",
    maxHeight: "85dvh"
});

const cardFullscreen = css({
    top: 0,
    bottom: 0,
    height: "100dvh"
});

// Clipping (rounded corners, scroll containment) is separate from `card` itself so the floating
// close button can be a sibling that overlaps the rounded top edge without being clipped by it
const cardClip = css({
    width: "100%",
    height: "100%",
    overflow: "hidden"
});

const cardHalfSheetRadius = css({
    borderRadius: "3.2rem 3.2rem 0 0"
});

const cardFullscreenRadius = css({
    borderRadius: 0
});

const halfSheetCloseButton = css({
    position: "absolute",
    top: 0,
    right: "50%",
    transform: "translate(50%, -50%)",
    zIndex: 1,
    width: "4rem",
    height: "4rem"
});

const overlay = css({
    boxSizing: "border-box",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    overflow: "auto",
    overscrollBehavior: "contain",
    background: "var(--colors-white)"
});

const contentLayer = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto"
});
import {type ReactNode, useLayoutEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {getAnalyticsSessionId} from "@pg-mono/algolytics";

import {CloseButton} from "../../atoms/chat_bot/feedback_popup/components/CloseButton";
import {SemanticSearchSurfacePhase} from "../state/semantic_search_modal_slice";
import {getSemanticSearchModalSize} from "../tracking/get_semantic_search_modal_size";
import {trackAmplitudeOpenAiChat} from "../tracking/track_amplitude_open_ai_chat";

interface Props {
    phase: SemanticSearchSurfacePhase;
    onClose: () => void;
    halfSheetChildren: ReactNode;
    children: (overlayRef: React.RefObject<HTMLDivElement | null>) => ReactNode;
}

export const SemanticSearchFullscreenOverlay = ({phase, onClose, halfSheetChildren, children}: Props) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const isOpen = phase !== "closed";
    const isFullscreen = phase === "fullscreen";

    const [hasFinishedGrowth, setHasFinishedGrowth] = useState(isFullscreen);
    const previousPhaseRef = useRef(phase);

    useLayoutEffect(() => {
        const previousPhase = previousPhaseRef.current;
        previousPhaseRef.current = phase;

        if (phase === "halfSheet") {
            setHasFinishedGrowth(false);
        } else if (phase === "fullscreen" && previousPhase !== "halfSheet") {
            setHasFinishedGrowth(true);
        }

        const modalSize = getSemanticSearchModalSize(phase);

        if (modalSize && phase !== previousPhase) {
            trackAmplitudeOpenAiChat(modalSize, getAnalyticsSessionId());
        }
    }, [phase]);

    useLayoutEffect(() => {
        if (!isFullscreen || !overlayRef.current) return;

        const element = overlayRef.current;
        element.scrollTop = element.scrollHeight;
    }, [isFullscreen]);

    useLayoutEffect(() => {
        if (!isOpen) return;

        const previousOverflow = document.body.style.overflow;
        const previousOverscrollBehavior = document.body.style.overscrollBehavior;

        document.body.style.overflow = "hidden";
        document.body.style.overscrollBehavior = "none";

        return () => {
            document.body.style.overflow = previousOverflow;
            document.body.style.overscrollBehavior = previousOverscrollBehavior;
        };
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className={backdrop}
                        onClick={!isFullscreen ? onClose : undefined}
                        initial={{opacity: 0}}
                        animate={{opacity: 0.75}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.3}}
                    />

                    <motion.div
                        layoutRoot
                        className={layoutRootStyle}
                        initial={{y: "100%"}}
                        animate={{y: 0}}
                        exit={{y: "100%"}}
                        transition={{type: "tween", duration: 0.25, ease: "easeOut"}}
                    >
                        <motion.div
                            layout
                            className={cx(card, isFullscreen ? cardFullscreen : cardHalfSheet)}
                            role="dialog"
                            aria-modal="true"
                            aria-label="Asystent AI"
                            transition={{type: "tween", duration: 0.25, ease: "easeOut"}}
                            onLayoutAnimationComplete={() => {
                                if (isFullscreen) {
                                    setHasFinishedGrowth(true);
                                }
                            }}
                        >
                            <div className={cx(cardClip, isFullscreen ? cardFullscreenRadius : cardHalfSheetRadius)}>
                                <div ref={overlayRef} className={overlay} data-testid="ai-advisor-modal">
                                    {!hasFinishedGrowth && (
                                        <motion.div className={contentLayer} animate={{opacity: isFullscreen ? 0 : 1}} transition={{duration: 0.15}}>
                                            {halfSheetChildren}
                                        </motion.div>
                                    )}

                                    {hasFinishedGrowth && (
                                        <motion.div className={contentLayer} initial={{opacity: 0}} animate={{opacity: 1}} transition={{duration: 0.15}}>
                                            {children(overlayRef)}
                                        </motion.div>
                                    )}
                                </div>
                            </div>

                            {!hasFinishedGrowth && <CloseButton onClose={onClose} className={halfSheetCloseButton} iconSize="2.4" />}
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

const backdrop = css({
    position: "fixed",
    inset: 0,
    zIndex: 9000,
    background: "var(--colors-secondary)"
});

const layoutRootStyle = css({
    position: "fixed",
    inset: 0,
    zIndex: 9001,
    pointerEvents: "none"
});

const card = css({
    position: "absolute",
    left: 0,
    right: 0,
    boxSizing: "border-box",
    pointerEvents: "auto"
});

// Mutually exclusive with cardFullscreen - both set top/bottom/height, never applied together
const cardHalfSheet = css({
    bottom: 0,
    top: "auto",
    height: "auto",
    maxHeight: "85dvh"
});

const cardFullscreen = css({
    top: 0,
    bottom: 0,
    height: "100dvh"
});

// Clipping (rounded corners, scroll containment) is separate from `card` itself so the floating
// close button can be a sibling that overlaps the rounded top edge without being clipped by it
const cardClip = css({
    width: "100%",
    height: "100%",
    overflow: "hidden"
});

const cardHalfSheetRadius = css({
    borderRadius: "3.2rem 3.2rem 0 0"
});

const cardFullscreenRadius = css({
    borderRadius: 0
});

const halfSheetCloseButton = css({
    position: "absolute",
    top: 0,
    right: "50%",
    transform: "translate(50%, -50%)",
    zIndex: 1,
    width: "4rem",
    height: "4rem"
});

const overlay = css({
    boxSizing: "border-box",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    overflow: "auto",
    overscrollBehavior: "contain",
    background: "var(--colors-white)"
});

const contentLayer = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto"
});
